import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAlert } from '../contexts/AlertContext';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Loader2, Mail, Plus, X } from 'lucide-react';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { SidePanel } from '../components/SidePanel';
import { nowLocal, todayLocal } from '../lib/date';
import { companyApi } from '../services/companyApi';
import {
  Board, Detail, ItemStatus, MAIL_KIND, REQ_STATUSES, ReportItem, ReqStatus, Request, Round, TERM, TERMS, reportApi } from '../services/reportApi';
import { Company } from '../types/company';

/**
 * 정기보고 — 투자처에 자료를 요청하고 받아 확정한다
 * (factsheet 소개서 11쪽 · 웨비나 「데이터룸」 편 대조, 2026-10-08 · 10-09).
 *
 * ⚠️ 투자처 계정이 없다(10/03 회의). factsheet 는 투자처가 화면에서 직접 채우지만 우리는
 *    **메일로 요청 → 받은 자료를 올림(제출) → 확인 · 코멘트 → 확정 또는 반려** 순서다.
 *    투자처 쪽 코멘트는 받은 메일을 옮겨 적는다.
 *
 * 상태 전이는 서버가 검사한다(#13). 화면은 지금 상태에서 할 수 있는 버튼만 보인다.
 * 알림 링크 `?round=&request=` 로 들어오면 그 회차 · 그 줄을 연다.
 */
const STYLE: Record<ReqStatus, string> = {
  '미요청': 'bg-slate-100 text-slate-500', '요청': 'bg-amber-50 text-amber-700', '제출': 'bg-sky-50 text-sky-700',
  '확정': 'bg-emerald-50 text-emerald-700', '반려': 'bg-red-50 text-red-600',
};
const ITEM_STYLE: Record<ItemStatus, string> = { '받음': 'text-emerald-700', '없음': 'text-red-600', '해당 없음': 'text-slate-500' };
const day = (iso: string | null) => (iso ? todayLocal(new Date(iso)).replace(/-/g, '.') : null);
const dot = (d: string | null) => (d ? d.replace(/-/g, '.') : '—');
const plusDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return todayLocal(d); };

export const PeriodicReportPage: React.FC = () => {
  const { showConfirm } = useAlert();
  const [params, setParams] = useSearchParams();
  const [board, setBoard] = useState<Board | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [commentCount, setCommentCount] = useState<Record<number, number>>({});
  const [roundId, setRoundId] = useState<number | null>(null);
  const [detail, setDetail] = useState<number | null>(null);
  const [wizard, setWizard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tourRun, setTourRun] = useState(false);

  const load = useCallback(async () => {
    const [b, c, cs] = await Promise.all([reportApi.board(), companyApi.list(), reportApi.commentCounts()]);
    setBoard(b); setCompanies(c); setCommentCount(cs);
  }, []);

  useEffect(() => { load().catch((e) => setErr(e.message)); }, [load]);
  // 알림 링크 ?round=&request= — 읽고 나면 지운다(다시 읽어도 사용자가 고른 회차를 덮지 않게)
  useEffect(() => {
    if (!board) return;
    const r = Number(params.get('round')), q = Number(params.get('request'));
    setRoundId((cur) => (board.rounds.some((x) => x.id === r) ? r : cur ?? board.rounds[0]?.id ?? null));
    if (board.requests.some((x) => x.id === q)) setDetail(q);
    if (r || q) setParams({}, { replace: true });
  }, [board, params, setParams]);

  const round = board?.rounds.find((r) => r.id === roundId) ?? null;
  const mine = useMemo(() => (board?.requests ?? []).filter((r) => r.roundId === roundId), [board, roundId]);
  const count = (s: ReqStatus) => mine.filter((r) => r.status === s).length;
  const company = (id: number) => companies.find((c) => c.id === id);
  const items = (r: Round) => (board?.items ?? []).filter((i) => r.itemIds.includes(i.id)).sort((a, b) => a.sortOrder - b.sortOrder);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr('');
    try { await fn(); } catch (e) { setErr((e as Error).message); }
    try { await load(); } finally { setBusy(false); }
  };

  /** 미요청 전부 — 메일 주소가 없는 곳은 건너뛰고 이름을 알려 준다 */
  const requestAll = () => run(async () => {
    const fails: string[] = [];
    for (const r of mine.filter((x) => x.status === '미요청')) {
      try { await reportApi.send(r.id); } catch (e) { fails.push(`${company(r.companyId)?.name} — ${(e as Error).message}`); }
    }
    if (fails.length) throw new Error(fails.join(' · '));
  });

  const openDetail = setDetail;

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'rounds', target: '[data-tour="rounds"]', title: '회차', content: '반기 · 분기마다 한 회차. 「새 회차」로 기간 · 항목 · 대상을 골라 만듭니다.' },
    { name: 'counts', target: '[data-tour="counts"]', title: '현황', content: '대상 · 미요청 · 요청 · 제출 · 확정 · 반려. 미요청을 한 번에 요청할 수 있습니다. 담당자 메일이 없는 곳은 건너뜁니다.' },
    { name: 'table', target: '[data-tour="table"]', title: '투자처별 상태', placement: 'top',
      content: '줄을 누르면 받은 자료 · 코멘트 · 메일 이력. 자료를 받으면 「자료 받음」(제출), 확인 뒤 확정하거나 사유 · 회신 기한을 붙여 반려합니다. 메일은 서버에서 켜져 있을 때만 나가고, 꺼져 있으면 이력에 「안 나감」으로 남습니다.' },
  ], []);

  if (!board) {
    return (
      <div className="p-6 lg:p-8">
        {err ? <p className="text-sm text-red-600">{err}</p>
          : <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>}
      </div>
    );
  }

  const req = mine.find((r) => r.id === detail);

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">정기보고 <TourButton onClick={() => setTourRun(true)} /></h1>
        <button onClick={() => setWizard(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700"><Plus size={13} /> 새 회차</button>
      </div>

      {err && !detail && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          <span>{err}</span><button onClick={() => setErr('')} aria-label="닫기"><X size={14} /></button>
        </div>
      )}

      {board.rounds.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 py-16 text-center text-sm text-slate-500">회차가 없습니다</div>
      ) : (
        <div data-tour="rounds" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-5">
          {board.rounds.map((r) => {
            const rs = board.requests.filter((x) => x.roundId === r.id);
            const done = rs.filter((x) => x.status === '확정').length;
            return (
              <button key={r.id} onClick={() => setRoundId(r.id)}
                className={`text-left bg-white border rounded-lg p-4 ${roundId === r.id ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-slate-200 hover:bg-slate-50'}`}>
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{r.title}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.status === '진행 중' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-700'}`}>{r.status}</span></div>
                <div className="mt-1 text-xs text-slate-500 tabular-nums">{dot(r.startOn)} – {dot(r.dueOn)}{r.includeRisk && ' · 리스크 평가 포함'}</div>
                <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${rs.length ? (done / rs.length) * 100 : 0}%` }} /></div>
                <div className="mt-1 text-xs text-slate-500 tabular-nums">확정 {done} / {rs.length}</div>
              </button>
            );
          })}
        </div>
      )}

      {round && (
        <>
          <div data-tour="counts" className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 flex-1">
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2"><div className="text-xs text-slate-400">대상</div><div className="text-lg font-bold tabular-nums">{mine.length}</div></div>
              {REQ_STATUSES.map((s) => (
                <div key={s} className="bg-white border border-slate-200 rounded-lg px-3 py-2"><div className="text-xs text-slate-400">{s}</div><div className="text-lg font-bold tabular-nums">{count(s)}</div></div>
              ))}
            </div>
            {round.status === '진행 중' && (
              <div className="flex gap-2">
                <button onClick={requestAll} disabled={busy || count('미요청') === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  <Mail size={12} /> 미요청 {count('미요청')}곳에 요청 메일
                </button>
                <button disabled={busy} onClick={async () => { if (await showConfirm(`${round.title} 회차를 종료합니다.\n종료하면 더 요청 · 제출을 받지 않습니다.`, { title: '회차 종료', confirmText: '종료', danger: true })) run(() => reportApi.close(round.id)); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40">회차 종료</button>
              </div>
            )}
          </div>

          <div data-tour="table" className="bg-white rounded-lg border border-slate-200 overflow-x-auto mb-5">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-xs text-slate-400">
                <th className="text-left font-medium px-4 py-2.5">투자처</th><th className="text-left font-medium px-2 py-2.5">담당자 메일</th>
                <th className="text-left font-medium px-2 py-2.5">요청</th><th className="text-left font-medium px-2 py-2.5">제출</th>
                <th className="text-left font-medium px-2 py-2.5">확정</th>
                <th className="text-left font-medium px-2 py-2.5">코멘트</th><th className="text-left font-medium px-4 py-2.5">상태</th><th />
              </tr></thead>
              <tbody>
                {mine.map((r) => {
                  const c = company(r.companyId);
                  return (
                    <tr key={r.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(r.id)}>
                      <td className="px-4 py-3 font-medium text-slate-900">{c?.name ?? r.companyId}</td>
                      <td className="px-2 py-3 text-xs">{c?.contactEmail ? <span className="text-slate-600">{c.contactEmail}</span> : <span className="text-red-500">없음</span>}</td>
                      <td className="px-2 py-3 text-xs tabular-nums text-slate-600">{day(r.sentAt) ?? '—'}</td>
                      <td className="px-2 py-3 text-xs tabular-nums text-slate-600">{day(r.submittedAt) ?? '—'}</td>
                      <td className="px-2 py-3 text-xs tabular-nums text-slate-600">{day(r.confirmedAt) ?? '—'}</td>
                      <td className="px-2 py-3 text-xs text-slate-600 tabular-nums">{commentCount[r.id] || '—'}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STYLE[r.status]}`}>{r.status}</span></td>
                      <td className="pr-3"><ChevronRight size={14} className="text-slate-300" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900 mb-2">요청 항목 {round.itemIds.length}</h2>
            <div className="flex flex-wrap gap-1.5">{items(round).map((t) => <span key={t.id} className="rounded-full px-2.5 py-1 text-xs bg-indigo-50 text-indigo-700">{t.name}</span>)}</div>
          </section>
        </>
      )}

      {round && req && (
        <DetailPanel key={req.id} round={round} req={req} company={company(req.companyId)} items={items(round)} busy={busy} err={err}
          onClose={() => { openDetail(null); setErr(''); }} run={run} reload={load} />
      )}
      {wizard && (
        <Wizard taken={board.rounds.map((r) => `${r.year}-${TERM.toUi(r.term) ?? r.term}`)} items={board.items.filter((i) => i.isActive)} companies={companies.filter((c) => c.status === 'active')}
          onClose={() => setWizard(false)}
          onCreate={async (f) => { const r = await reportApi.create(f); await load(); setRoundId(r.id); setWizard(false); }} />
      )}
      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};

const DetailPanel: React.FC<{
  round: Round; req: Request; company: Company | undefined; items: ReportItem[]; busy: boolean; err: string;
  onClose: () => void; run: (fn: () => Promise<unknown>) => Promise<void>; reload: () => Promise<unknown>;
}> = ({ round, req, company, items, busy, err, onClose, run, reload }) => {
  const [d, setD] = useState<Detail | null>(null);
  const [text, setText] = useState('');
  const [side, setSide] = useState<'우리' | '투자처'>('우리');
  const [reject, setReject] = useState(false);
  const [reason, setReason] = useState('');
  const [due, setDue] = useState(plusDays(7));
  const name = company?.name ?? String(req.companyId);
  const open = round.status === '진행 중';
  const editable = open && ['요청', '제출', '반려'].includes(req.status);

  const fetchDetail = useCallback(() => reportApi.detail(req.id).then(setD), [req.id]);
  useEffect(() => { fetchDetail(); }, [fetchDetail, req.status]);
  const act = (fn: () => Promise<unknown>) => run(fn).then(fetchDetail);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose}>
      <aside className="w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-bold text-slate-900">{name} · {round.title}</h2>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STYLE[req.status]}`}>{req.status}</span>
            <Link to={`/companies/${req.companyId}`} className="ml-2 text-xs text-indigo-600 hover:underline">투자처 상세</Link>
          </div>
          <button onClick={onClose} aria-label="닫기" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {req.status === '반려' && req.rejectReason && (
          <div className="mx-5 mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{req.rejectReason}<span className="ml-1 text-xs tabular-nums">· 회신 {dot(req.replyDueOn)}</span></div>
        )}

        <section className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-xs font-semibold text-slate-500 mb-2">받은 자료 {d ? Object.values(d.items).filter((s) => s === '받음').length : 0} / {items.length}</h3>
          <ul className="space-y-1">
            {items.map((t) => {
              const cur = d?.items[t.id];
              return (
                <li key={t.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 rounded-md px-2.5 py-1.5">
                  <span>{t.name}</span>
                  {editable ? (
                    <span className="flex gap-0.5" role="group" aria-label={t.name}>
                      {(['받음', '없음', '해당 없음'] as ItemStatus[]).map((s) => (
                        <button key={s} disabled={busy} onClick={() => act(() => reportApi.setItem(req.id, t.id, s))}
                          className={`px-1.5 py-0.5 rounded text-[11px] border ${cur === s ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-200 text-slate-500 bg-white'}`}>{s}</button>
                      ))}
                    </span>
                  ) : <span className={`text-xs font-medium ${cur ? ITEM_STYLE[cur] : 'text-slate-400'}`}>{cur ?? '—'}</span>}
                </li>
              );
            })}
          </ul>
        </section>

        <section className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-xs font-semibold text-slate-500 mb-2">코멘트 {d?.comments.length ?? 0}</h3>
          <ul className="space-y-2 mb-3">
            {d?.comments.map((c) => (
              <li key={c.id} className={`rounded-lg px-3 py-2 text-sm ${c.side === '우리' ? 'bg-indigo-50' : 'bg-slate-50'}`}>
                <div className="text-xs text-slate-500 mb-0.5">{c.authorName} · <span className="tabular-nums">{nowLocal(new Date(c.createdAt))}</span></div>{c.body}
              </li>
            ))}
          </ul>
          <div className="flex gap-1 mb-1.5">
            {(['우리', '투자처'] as const).map((s) => (
              <button key={s} onClick={() => setSide(s)} className={`px-2 py-0.5 rounded-md text-xs border ${side === s ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-200 text-slate-600'}`}>{s === '우리' ? '우리가 씀' : '받은 메일 옮겨 적기'}</button>
            ))}
          </div>
          <label className="sr-only" htmlFor="report-comment">코멘트</label>
          <textarea id="report-comment" value={text} onChange={(e) => setText(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg h-16 resize-none" />
          <button disabled={busy || !text.trim()}
            onClick={() => act(async () => { await reportApi.addComment(req.id, text.trim(), side, side === '우리' ? '김성배' : `${name} (메일)`); setText(''); await reload(); })}
            className="mt-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-900 text-white disabled:opacity-40">코멘트 남기기</button>
        </section>

        {d && d.mails.length > 0 && (
          <section className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-xs font-semibold text-slate-500 mb-2">메일 {d.mails.length}</h3>
            <ul className="space-y-1 text-xs">
              {d.mails.map((m) => (
                <li key={m.id} className="flex items-center gap-2 tabular-nums" title={m.error ?? m.subject}>
                  <span className="text-slate-500">{nowLocal(new Date(m.sentAt))}</span>
                  <span className="text-slate-700">{MAIL_KIND.toUi(m.kind) ?? m.kind}</span>
                  <span className="text-slate-500 truncate">{m.toEmail}</span>
                  <span className={`ml-auto font-medium ${m.status === 'sent' ? 'text-emerald-700' : 'text-red-600'}`}>{m.status === 'sent' ? '보냄' : m.status === 'bounced' ? '반송' : '안 나감'}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="px-5 py-4">
          {err && <p className="mb-2 text-xs text-red-600">{err}</p>}
          {!open ? <p className="text-xs text-slate-400">종료한 회차</p> : reject ? (
            <div className="space-y-2">
              <label className="block text-xs text-slate-500" htmlFor="reject-reason">반려 사유</label>
              <textarea id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg h-16 resize-none" />
              <label className="block text-xs text-slate-500" htmlFor="reject-due">회신 기한</label>
              <input id="reject-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg" />
              <div className="flex gap-2 pt-1">
                <button onClick={() => setReject(false)} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300">취소</button>
                <button disabled={busy || !reason.trim() || !due} onClick={() => act(() => reportApi.reject(req.id, reason.trim(), due)).then(() => setReject(false))}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white disabled:opacity-40">반려</button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              {(req.status === '미요청' || req.status === '반려') && (
                <button disabled={busy} onClick={() => act(() => reportApi.send(req.id))}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"><Mail size={14} /> {req.status === '반려' ? '다시 요청' : '요청 메일'}</button>
              )}
              {(req.status === '요청' || req.status === '반려') && (
                <button disabled={busy} onClick={() => act(() => reportApi.submit(req.id))}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40">자료 받음</button>
              )}
              {req.status === '제출' && (
                <>
                  <button disabled={busy} onClick={() => setReject(true)} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40">반려</button>
                  <button disabled={busy} onClick={() => act(() => reportApi.confirm(req.id))} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">확정</button>
                </>
              )}
              {req.status === '확정' && <p className="text-xs text-slate-400 tabular-nums">확정 {day(req.confirmedAt)}</p>}
            </div>
          )}
        </section>
      </aside>
    </div>
  );
};

type CreateForm = Parameters<typeof reportApi.create>[0];

const Wizard: React.FC<{ taken: string[]; items: ReportItem[]; companies: Company[]; onClose: () => void; onCreate: (f: CreateForm) => Promise<void> }> = ({ taken, items, companies, onClose, onCreate }) => {
  const [step, setStep] = useState(1);
  // 7월부터는 그해 상반기, 그 전에는 지난해 하반기를 받는다 — 그 회차가 이미 있으면 다음 반기로 넘긴다
  const first = useMemo(() => {
    const now = new Date();
    let y = now.getFullYear() - (now.getMonth() >= 6 ? 0 : 1);
    let t: '상반기' | '하반기' = now.getMonth() >= 6 ? '상반기' : '하반기';
    for (let i = 0; i < 6 && taken.includes(`${y}-${t}`); i++) { if (t === '상반기') t = '하반기'; else { t = '상반기'; y += 1; } }
    return { y, t };
  }, [taken]);
  const [year, setYear] = useState(first.y);
  const [term, setTerm] = useState<string>(first.t);
  const exists = taken.includes(`${year}-${term}`);
  const [from, setFrom] = useState(todayLocal());
  const [to, setTo] = useState('');
  const [withRisk, setWithRisk] = useState(true);
  const [itemIds, setItemIds] = useState<number[]>(items.map((i) => i.id));
  const [targets, setTargets] = useState<number[]>(companies.map((c) => c.id));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const ok1 = from && to && to >= from;
  const steps = ['기간', '항목', '대상', '확인'];
  const toggle = (set: React.Dispatch<React.SetStateAction<number[]>>, id: number, on: boolean) => set((t) => (on ? [...t, id] : t.filter((x) => x !== id)));

  const submit = async () => {
    setSaving(true); setErr('');
    try { await onCreate({ year, term, startOn: from, dueOn: to || null, includeRisk: withRisk, companyIds: targets, itemIds }); }
    catch (e) { setErr((e as Error).message); setSaving(false); }
  };

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">새 정기보고 회차</h2>
            <ol className="mt-1 flex gap-2 text-xs">{steps.map((s, i) => <li key={s} className={i + 1 === step ? 'font-semibold text-indigo-600' : 'text-slate-400'}>{i + 1}. {s}</li>)}</ol>
          </div>
          <button onClick={onClose} aria-label="닫기" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3 min-h-[200px]">
          {step === 1 && (
            <>
              <div className="flex gap-2">
                <label className="text-xs text-slate-500">연도<input id="wz-year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="block mt-1 w-24 px-2 py-1.5 text-sm border border-slate-200 rounded-lg" /></label>
                <label className="text-xs text-slate-500">구분<select id="wz-term" value={term} onChange={(e) => setTerm(e.target.value)} className="block mt-1 px-2 py-1.5 text-sm border border-slate-200 rounded-lg">{TERMS.map((t) => <option key={t}>{t}</option>)}</select></label>
              </div>
              {exists && <p className="text-xs text-red-600">{year} {term} 회차가 이미 있습니다</p>}
              <div className="flex gap-2">
                <label className="text-xs text-slate-500">요청 시작일<input id="wz-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block mt-1 px-2 py-1.5 text-sm border border-slate-200 rounded-lg" /></label>
                <label className="text-xs text-slate-500">제출 마감일<input id="wz-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block mt-1 px-2 py-1.5 text-sm border border-slate-200 rounded-lg" /></label>
              </div>
              <p className="text-xs text-slate-400">보고 대상 기간은 위 연도 · 구분({year} {term})입니다. 아래 두 날짜는 투자처에 자료를 요청하고 받는 기간입니다.</p>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={withRisk} onChange={(e) => setWithRisk(e.target.checked)} /> 확정되면 리스크 평가 대상에 넣기</label>
            </>
          )}
          {step === 2 && (
            <ul className="grid grid-cols-2 gap-1.5">
              {items.map((i) => (
                <li key={i.id}><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={itemIds.includes(i.id)} onChange={(e) => toggle(setItemIds, i.id, e.target.checked)} />{i.name}</label></li>
              ))}
            </ul>
          )}
          {step === 3 && (
            <ul className="space-y-1.5">
              {companies.map((c) => (
                <li key={c.id}><label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={targets.includes(c.id)} onChange={(e) => toggle(setTargets, c.id, e.target.checked)} />{c.name}
                  {!c.contactEmail && <span className="text-xs text-red-500">메일 없음</span>}
                </label></li>
              ))}
            </ul>
          )}
          {step === 4 && (
            <dl className="grid grid-cols-[90px_1fr] gap-y-1.5 text-sm">
              <dt className="text-slate-500">보고 기간</dt><dd className="font-medium">{year} {term}</dd>
              <dt className="text-slate-500">요청 기간</dt><dd className="tabular-nums">{dot(from)} – {dot(to)} <span className="text-slate-400">(제출 마감)</span></dd>
              <dt className="text-slate-500">항목</dt><dd>{itemIds.length}</dd>
              <dt className="text-slate-500">대상</dt><dd>{companies.filter((c) => targets.includes(c.id)).map((c) => c.name).join(' · ')} ({targets.length})</dd>
              <dt className="text-slate-500">리스크</dt><dd>{withRisk ? '확정 뒤 평가 대상' : '넣지 않음'}</dd>
            </dl>
          )}
          {err && <p className="text-xs text-red-600">{err}</p>}
        </div>
        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-between gap-2 px-5 py-4 border-t border-slate-100">
          <button onClick={() => (step === 1 ? onClose() : setStep(step - 1))} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">{step === 1 ? '취소' : '이전'}</button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} disabled={(step === 1 && !ok1) || (step === 2 && itemIds.length === 0) || (step === 3 && targets.length === 0)} className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white disabled:opacity-40">다음</button>
          ) : (
            <button onClick={submit} disabled={saving || exists} className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white disabled:opacity-40">회차 만들기</button>
          )}
        </div>
      </div>
    </SidePanel>
  );
};
